import { useEffect, useMemo, useRef, useState } from "react";
import { cn } from "@/lib/utils";

const VERTEX_SHADER_SOURCE = `#version 300 es
in vec2 a_position;

void main() {
  gl_Position = vec4(a_position, 0.0, 1.0);
}
`;

const FRAGMENT_SHADER_SOURCE = `#version 300 es
precision highp float;

uniform vec2 u_resolution;
uniform float u_time;
uniform float u_opacity;
uniform float u_cell_size;
uniform float u_angle;
uniform vec3 u_line_color;

out vec4 outColor;

void main() {
  vec2 resolution = max(u_resolution, vec2(1.0));
  vec2 uv = gl_FragCoord.xy / resolution;

  float horizon = 0.92;
  float belowHorizon = horizon - uv.y;
  float horizonMask = 1.0 - smoothstep(horizon - 0.01, horizon + 0.08, uv.y);

  float angleNorm = clamp(u_angle / 89.0, 0.05, 0.98);
  float perspectivePower = mix(1.9, 0.95, angleNorm);
  float depth = max(pow(max(belowHorizon, 0.001), perspectivePower), 0.001);

  float aspect = resolution.x / resolution.y;
  float worldX = ((uv.x - 0.5) * 2.0 * aspect) / depth;
  float worldZ = (1.0 / depth) + (u_time * 0.5);

  float frequency = 60.0 / max(u_cell_size, 4.0);
  vec2 gridUv = vec2(worldX, worldZ) * frequency;
  vec2 fw = max(fwidth(gridUv), vec2(0.0001));
  vec2 gridDist = abs(fract(gridUv - 0.5) - 0.5) / fw;
  float line = 1.0 - clamp(min(gridDist.x, gridDist.y), 0.0, 1.0);

  // Fade far lines toward the horizon so opacity increases with viewer proximity.
  float distanceNorm = clamp(belowHorizon / horizon, 0.0, 1.0);
  float distanceFade = smoothstep(0.08, 0.85, distanceNorm);
  distanceFade = pow(distanceFade, 1.7);
  float alpha = line * horizonMask * distanceFade * clamp(u_opacity, 0.0, 1.0);

  outColor = vec4(u_line_color, alpha);
}
`;

let sharedColorContext: CanvasRenderingContext2D | null = null;

function clamp01(value: number) {
  return Math.max(0, Math.min(1, value));
}

function prefersCssFallbackByPlatform() {
  if (typeof navigator === "undefined") {
    return false;
  }

  const { userAgent, platform, maxTouchPoints } = navigator;
  const isIosDevice =
    /iPad|iPhone|iPod/.test(userAgent) ||
    (platform === "MacIntel" && maxTouchPoints > 1);
  const isAppleWebKit =
    /AppleWebKit/i.test(userAgent) &&
    !/CriOS|FxiOS|EdgiOS|OPiOS/i.test(userAgent);

  return isIosDevice && isAppleWebKit;
}

function getColorContext() {
  if (sharedColorContext || typeof document === "undefined") {
    return sharedColorContext;
  }

  const canvas = document.createElement("canvas");
  sharedColorContext = canvas.getContext("2d");
  return sharedColorContext;
}

function parseHexColor(value: string): [number, number, number] | null {
  const hex = value.trim().toLowerCase();
  if (!hex.startsWith("#")) {
    return null;
  }

  if (hex.length === 4) {
    const r = Number.parseInt(`${hex[1]}${hex[1]}`, 16);
    const g = Number.parseInt(`${hex[2]}${hex[2]}`, 16);
    const b = Number.parseInt(`${hex[3]}${hex[3]}`, 16);
    if ([r, g, b].some((channel) => Number.isNaN(channel))) {
      return null;
    }

    return [r / 255, g / 255, b / 255];
  }

  if (hex.length === 7) {
    const r = Number.parseInt(hex.slice(1, 3), 16);
    const g = Number.parseInt(hex.slice(3, 5), 16);
    const b = Number.parseInt(hex.slice(5, 7), 16);
    if ([r, g, b].some((channel) => Number.isNaN(channel))) {
      return null;
    }

    return [r / 255, g / 255, b / 255];
  }

  return null;
}

function parseRgbColor(value: string): [number, number, number] | null {
  const match = value.match(/^rgba?\((.+)\)$/i);
  if (!match) {
    return null;
  }

  const channels = match[1]
    .split(",")
    .slice(0, 3)
    .map((part) => Number.parseFloat(part.trim()));

  if (
    channels.length !== 3 ||
    channels.some((channel) => Number.isNaN(channel))
  ) {
    return null;
  }

  return [
    clamp01(channels[0] / 255),
    clamp01(channels[1] / 255),
    clamp01(channels[2] / 255),
  ];
}

function parseCssColorToRgb(color: string): [number, number, number] | null {
  if (typeof document === "undefined") {
    return null;
  }

  if (
    typeof CSS !== "undefined" &&
    typeof CSS.supports === "function" &&
    !CSS.supports("color", color)
  ) {
    return null;
  }

  const context = getColorContext();
  if (!context) {
    return null;
  }

  context.fillStyle = "#010203";
  context.fillStyle = color;

  const normalized = context.fillStyle.toLowerCase();
  return parseHexColor(normalized) ?? parseRgbColor(normalized);
}

function compileShader(
  gl: WebGL2RenderingContext,
  type: number,
  source: string,
) {
  const shader = gl.createShader(type);
  if (!shader) {
    return null;
  }

  gl.shaderSource(shader, source);
  gl.compileShader(shader);

  if (gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
    return shader;
  }

  if (import.meta.env.DEV) {
    console.error(
      "RetroGrid shader compile error",
      gl.getShaderInfoLog(shader),
    );
  }

  gl.deleteShader(shader);
  return null;
}

function createProgram(gl: WebGL2RenderingContext) {
  const vertexShader = compileShader(
    gl,
    gl.VERTEX_SHADER,
    VERTEX_SHADER_SOURCE,
  );
  const fragmentShader = compileShader(
    gl,
    gl.FRAGMENT_SHADER,
    FRAGMENT_SHADER_SOURCE,
  );

  if (!vertexShader || !fragmentShader) {
    if (vertexShader) {
      gl.deleteShader(vertexShader);
    }
    if (fragmentShader) {
      gl.deleteShader(fragmentShader);
    }
    return null;
  }

  const program = gl.createProgram();
  if (!program) {
    gl.deleteShader(vertexShader);
    gl.deleteShader(fragmentShader);
    return null;
  }

  gl.attachShader(program, vertexShader);
  gl.attachShader(program, fragmentShader);
  gl.linkProgram(program);
  gl.deleteShader(vertexShader);
  gl.deleteShader(fragmentShader);

  if (gl.getProgramParameter(program, gl.LINK_STATUS)) {
    return program;
  }

  if (import.meta.env.DEV) {
    console.error(
      "RetroGrid program link error",
      gl.getProgramInfoLog(program),
    );
  }

  gl.deleteProgram(program);
  return null;
}

function setGlProgram(
  gl: WebGL2RenderingContext,
  program: WebGLProgram | null,
) {
  gl.useProgram(program);
}

function RetroGridCssFallback({
  angle,
  cellSize,
  lightLineColor,
  darkLineColor,
}: {
  angle: number;
  cellSize: number;
  lightLineColor: string;
  darkLineColor: string;
}) {
  const fallbackAngle = Math.min(angle, 89);
  const fallbackStyles = {
    "--grid-angle": `${fallbackAngle}deg`,
    "--cell-size": `${cellSize / 5}px`,
    "--light-line": lightLineColor,
    "--dark-line": darkLineColor,
  } as React.CSSProperties;

  return (
    <div className="absolute inset-0" style={fallbackStyles}>
      <div className="absolute inset-0 w-full max-h-1/2 bg-linear-to-b from-[#002D21] to-transparent z-10" />
      <div className="absolute inset-0 origin-top transform-[perspective(1200px)_rotateX(var(--grid-angle))]">
        <div className="animate-grid inset-[0%_0px] ml-[-200%] h-[300vh] w-[600vw] origin-[100%_0_0] bg-[linear-gradient(to_right,var(--light-line)_0.5px,transparent_0),linear-gradient(to_bottom,var(--light-line)_0.5px,transparent_0)] bg-size-[var(--cell-size)_var(--cell-size)] bg-repeat dark:bg-[linear-gradient(to_right,var(--dark-line)_0.5px,transparent_0),linear-gradient(to_bottom,var(--dark-line)_0.5px,transparent_0)]" />
      </div>
    </div>
  );
}

interface RetroGridProps extends React.HTMLAttributes<HTMLDivElement> {
  /**
   * Additional CSS classes to apply to the grid container
   */
  className?: string;
  /**
   * Rotation angle of the grid in degrees
   * @default 65
   */
  angle?: number;
  /**
   * Grid cell size in pixels
   * @default 60
   */
  cellSize?: number;
  /**
   * Grid opacity value between 0 and 1
   * @default 0.5
   */
  opacity?: number;
  /**
   * Grid line color in light mode
   * @default "gray"
   */
  lightLineColor?: string;
  /**
   * Grid line color in dark mode
   * @default "gray"
   */
  darkLineColor?: string;
}

export function RetroGrid({
  className,
  angle = 65,
  cellSize = 60,
  opacity = 0.5,
  lightLineColor = "gray",
  darkLineColor = "gray",
  ...props
}: RetroGridProps) {
  const { style, ...restProps } = props;
  const containerRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [useCssFallback, setUseCssFallback] = useState(() =>
    prefersCssFallbackByPlatform(),
  );
  const [isDarkMode, setIsDarkMode] = useState(() =>
    typeof document !== "undefined"
      ? document.documentElement.classList.contains("dark")
      : false,
  );
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(() =>
    typeof window !== "undefined"
      ? window.matchMedia("(prefers-reduced-motion: reduce)").matches
      : false,
  );

  const activeLineColor = isDarkMode ? darkLineColor : lightLineColor;
  const normalizedLineColor = useMemo(() => {
    const parsed = parseCssColorToRgb(activeLineColor);
    if (parsed) {
      return parsed;
    }

    if (import.meta.env.DEV) {
      console.warn(
        `RetroGrid: failed to parse color "${activeLineColor}", falling back to gray.`,
      );
    }

    return [0.5, 0.5, 0.5] as const;
  }, [activeLineColor]);

  const rootStyles = {
    "--opacity": opacity,
    ...(style ?? {}),
  } as React.CSSProperties;

  useEffect(() => {
    if (
      typeof document === "undefined" ||
      typeof MutationObserver === "undefined"
    ) {
      return;
    }

    const root = document.documentElement;
    const updateTheme = () => {
      setIsDarkMode(root.classList.contains("dark"));
    };

    updateTheme();
    const observer = new MutationObserver(updateTheme);
    observer.observe(root, { attributes: true, attributeFilter: ["class"] });

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    const mediaQuery = window.matchMedia("(prefers-reduced-motion: reduce)");
    const handleMotionChange = () => {
      setPrefersReducedMotion(mediaQuery.matches);
    };

    handleMotionChange();
    mediaQuery.addEventListener("change", handleMotionChange);

    return () => mediaQuery.removeEventListener("change", handleMotionChange);
  }, []);

  useEffect(() => {
    if (useCssFallback) {
      return;
    }

    const container = containerRef.current;
    const canvas = canvasRef.current;
    if (!container || !canvas) {
      return;
    }

    let fallbackQueued = false;
    let isDisposed = false;
    const queueFallback = () => {
      if (fallbackQueued || isDisposed) {
        return;
      }

      fallbackQueued = true;
      queueMicrotask(() => {
        if (!isDisposed) {
          setUseCssFallback(true);
        }
      });
    };

    const gl = canvas.getContext("webgl2", {
      alpha: true,
      antialias: false,
      depth: false,
      stencil: false,
      powerPreference: "high-performance",
    });

    if (!gl) {
      queueFallback();
      return;
    }

    const program = createProgram(gl);
    if (!program) {
      queueFallback();
      return;
    }

    const positionBuffer = gl.createBuffer();
    if (!positionBuffer) {
      gl.deleteProgram(program);
      queueFallback();
      return;
    }

    const positionAttribute = gl.getAttribLocation(program, "a_position");
    const resolutionUniform = gl.getUniformLocation(program, "u_resolution");
    const timeUniform = gl.getUniformLocation(program, "u_time");
    const opacityUniform = gl.getUniformLocation(program, "u_opacity");
    const cellSizeUniform = gl.getUniformLocation(program, "u_cell_size");
    const angleUniform = gl.getUniformLocation(program, "u_angle");
    const lineColorUniform = gl.getUniformLocation(program, "u_line_color");

    gl.bindBuffer(gl.ARRAY_BUFFER, positionBuffer);
    gl.bufferData(
      gl.ARRAY_BUFFER,
      new Float32Array([-1, -1, 1, -1, -1, 1, 1, 1]),
      gl.STATIC_DRAW,
    );

    let rafId = 0;
    let resizeObserver: ResizeObserver | null = null;
    let contextLost = false;

    const renderFrame = (elapsedSeconds: number) => {
      if (contextLost || isDisposed) {
        return;
      }

      const width = Math.max(1, canvas.width);
      const height = Math.max(1, canvas.height);

      gl.viewport(0, 0, width, height);
      gl.clearColor(0, 0, 0, 0);
      gl.clear(gl.COLOR_BUFFER_BIT);
      setGlProgram(gl, program);

      gl.bindBuffer(gl.ARRAY_BUFFER, positionBuffer);
      gl.enableVertexAttribArray(positionAttribute);
      gl.vertexAttribPointer(positionAttribute, 2, gl.FLOAT, false, 0, 0);

      gl.uniform2f(resolutionUniform, width, height);
      gl.uniform1f(timeUniform, elapsedSeconds);
      gl.uniform1f(opacityUniform, opacity);
      gl.uniform1f(cellSizeUniform, cellSize);
      gl.uniform1f(angleUniform, angle);
      gl.uniform3f(
        lineColorUniform,
        normalizedLineColor[0],
        normalizedLineColor[1],
        normalizedLineColor[2],
      );

      gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
    };

    const syncSize = () => {
      const rect = container.getBoundingClientRect();
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      const width = Math.max(1, Math.round(rect.width * dpr));
      const height = Math.max(1, Math.round(rect.height * dpr));

      if (canvas.width !== width || canvas.height !== height) {
        canvas.width = width;
        canvas.height = height;
      }

      canvas.style.width = `${rect.width}px`;
      canvas.style.height = `${rect.height}px`;
    };

    let start = performance.now();
    const animate = (now: number) => {
      if (isDisposed || contextLost) {
        return;
      }

      renderFrame((now - start) / 1000);
      rafId = window.requestAnimationFrame(animate);
    };

    const handleContextLost = (event: Event) => {
      event.preventDefault();
      contextLost = true;
      if (rafId) {
        window.cancelAnimationFrame(rafId);
      }
    };

    const handleContextRestored = () => {
      if (isDisposed) {
        return;
      }

      contextLost = false;
      start = performance.now();
      syncSize();
      if (prefersReducedMotion) {
        renderFrame(0);
        return;
      }
      rafId = window.requestAnimationFrame(animate);
    };

    canvas.addEventListener("webglcontextlost", handleContextLost);
    canvas.addEventListener("webglcontextrestored", handleContextRestored);

    syncSize();
    if (typeof ResizeObserver !== "undefined") {
      resizeObserver = new ResizeObserver(() => {
        syncSize();
        renderFrame(0);
      });
      resizeObserver.observe(container);
    } else {
      window.addEventListener("resize", syncSize);
    }

    if (prefersReducedMotion) {
      renderFrame(0);
    } else {
      rafId = window.requestAnimationFrame(animate);
    }

    return () => {
      isDisposed = true;
      if (rafId) {
        window.cancelAnimationFrame(rafId);
      }
      canvas.removeEventListener("webglcontextlost", handleContextLost);
      canvas.removeEventListener("webglcontextrestored", handleContextRestored);
      if (resizeObserver) {
        resizeObserver.disconnect();
      } else {
        window.removeEventListener("resize", syncSize);
      }

      gl.bindBuffer(gl.ARRAY_BUFFER, null);
      setGlProgram(gl, null);
      gl.deleteBuffer(positionBuffer);
      gl.deleteProgram(program);
    };
  }, [
    angle,
    cellSize,
    normalizedLineColor,
    opacity,
    prefersReducedMotion,
    useCssFallback,
  ]);

  return (
    <div
      ref={containerRef}
      className={cn(
        "pointer-events-none absolute size-full overflow-hidden perspective-normal",
        `opacity-(--opacity)`,
        className,
      )}
      style={rootStyles}
      {...restProps}
    >
      {useCssFallback ? (
        <RetroGridCssFallback
          angle={angle}
          cellSize={cellSize}
          lightLineColor={lightLineColor}
          darkLineColor={darkLineColor}
        />
      ) : (
        <canvas ref={canvasRef} className="absolute inset-0 h-full w-full" />
      )}
      <div className="absolute inset-0 bg-linear-to-t from-black to-transparent to-90%" />
    </div>
  );
}
